import { useState, useRef, useEffect, useCallback } from "react";
import BotResponse from "./BotResponse";

const API_URL    = process.env.REACT_APP_API_CHAT_URL || "http://127.0.0.1:8000/chat";
const SESSION_PREFIX = process.env.REACT_APP_SESSION_ID || "chat";

const MAX_MESSAGE_LENGTH = 2000;    // the backend's limit (ChatRequest.message)
const REQUEST_TIMEOUT_MS = 60000;

// The error the backend sent: { detail: { message, detail, code } } for an AI provider
// error, { detail: "…" } from the server, or FastAPI's list of field errors (422)
function responseError(status, data) {
  const detail = data?.detail;
  let message = null;
  let extra = {};
  if (Array.isArray(detail)) {
    message = detail.some((d) => d?.loc?.includes("message") && d?.type === "string_too_long")
      ? `Your message is too long — please keep it under ${MAX_MESSAGE_LENGTH.toLocaleString()} characters.`
      : "That message couldn't be sent. Please rephrase it and try again.";
  } else if (detail && typeof detail === "object") {
    message = detail.message;
    extra = detail;
  } else if (typeof detail === "string") {
    message = detail;
  }
  const error = new Error(message || `Server error ${status}. Please try again.`);
  error.detail = extra.detail || null;
  error.code = extra.code || null;
  return error;
}

// The conversation is kept in this browser, so a reload doesn't wipe it
const STORAGE_KEY = "sl-chat-v1";
const MAX_SAVED_MESSAGES = 40;

function loadSavedChat() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null");
    if (saved?.sessionId && Array.isArray(saved.messages) && saved.messages.length) {
      // Messages that were still waiting when the page closed were never sent
      return { ...saved, messages: saved.messages.map((m) => (m.queued ? { ...m, queued: false, notSent: true } : m)) };
    }
  } catch {
    // storage blocked or unreadable: start fresh
  }
  return null;
}

function saveChat(sessionId, messages) {
  const keep = messages.slice(-MAX_SAVED_MESSAGES);
  for (const batch of [keep, keep.slice(-10)]) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ sessionId, messages: batch }));
      return;
    } catch {
      // storage full (stone cards are large) or blocked: try fewer messages
    }
  }
}

// What this chat window shows, sent with every message: if the server has forgotten the chat
// (an hour idle, a restart) it carries on from it — "go back to the cushions", "the first one"
function chatContext(messages) {
  const answers = messages.filter((m) => m.role === "bot" && m.data);
  const searches = answers
    .filter((m) => m.data.type === "stone_cards" && Object.keys(m.data.filters_applied || {}).length)
    .map((m) => m.data.filters_applied)
    .slice(-8);
  const lastList = [...answers].reverse().find((m) => m.data.stones?.length);
  const shown = (lastList?.data.stones || []).map((s) => s.stock_id).filter(Boolean).slice(0, 24);
  return { recent_searches: searches, shown_stone_ids: shown };
}

// The main SL Diamond website opens the chat with its login token (…?token=…). It is kept for
// this tab, taken out of the address bar, and sent with every message so the chat knows who is
// logged in ("recommendations based on my preferences")
const TOKEN_KEY = "sl-chat-token";
let tabToken = null;   // when session storage is blocked: kept until the page reloads

function loginToken() {
  const url = new URL(window.location.href);
  const fromUrl = url.searchParams.get("token");
  if (fromUrl) {
    tabToken = fromUrl;
    url.searchParams.delete("token");
    window.history.replaceState(window.history.state, "", url.href);
  }
  try {
    if (fromUrl) window.sessionStorage.setItem(TOKEN_KEY, fromUrl);
    return window.sessionStorage.getItem(TOKEN_KEY) || tabToken;
  } catch {
    return tabToken;
  }
}

function createSessionId() {
  const uniqueId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${SESSION_PREFIX}-${uniqueId}`;
}

const WELCOME = {
  role: "bot",
  id: 0,
  data: {
    message: "I'm your AI Analytics Assistant. I can help you analyze business metrics, compare regional performance, search for diamonds, and predict future trends. How can I help you today?",
    clarify_options: [
      "Show total sales this year",
      "Top salespersons by revenue",
      "Compare Belgium vs Hong Kong",
      "Show inventory by shape",
    ],
  },
};
// Shown when the backend reports analytics switched off (ANALYTICS_ENABLED=false)
const WELCOME_SEARCH_ONLY = {
  role: "bot",
  id: 0,
  data: {
    message: "I'm your AI Diamond Assistant. I can search the inventory, look up a stone by its ID, compare stones, and find similar stones or matching pairs. How can I help you today?",
    clarify_options: [
      "Show me round diamonds around 1 carat",
      "Fancy yellow diamonds",
      "Oval diamonds under $5,000",
      "What is the difference between VS1 and VS2 clarity",
    ],
  },
};
const HEALTH_URL = API_URL.replace(/\/chat\/?$/, "/health");

// ── Reply preview bar shown above the input when a message is quoted ──────────
function ReplyBar({ reply, onCancel }) {
  if (!reply) return null;

  const icon =
    reply.type === "search" ? "💎" :
    reply.type === "analytics" ? "📊" : "💬";

  return (
    <div className="mx-6 mb-2 flex items-start gap-2 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg px-3 py-2">
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-blue-600 mb-0.5 flex items-center gap-1">
          {icon} Replying to
        </div>
        <p className="text-xs text-gray-600 truncate">{reply.preview}</p>
      </div>
      <button
        onClick={onCancel}
        className="text-gray-400 hover:text-gray-700 transition-colors text-sm flex-shrink-0 mt-0.5"
        title="Cancel reply"
      >
        ✕
      </button>
    </div>
  );
}

export default function ChatWindow({ onOpenSearch }) {
  const [savedChat] = useState(loadSavedChat);
  const [sessionId, setSessionId] = useState(() => savedChat?.sessionId || createSessionId());
  const [welcome, setWelcome] = useState(WELCOME);
  const [analyticsOff, setAnalyticsOff] = useState(false);
  const [messages, setMessages] = useState(() => savedChat?.messages || [WELCOME]);

  // Take the login token out of the address bar as soon as the chat opens
  useEffect(() => {
    loginToken();
  }, []);

  // Ask the backend whether analytics are on; swap the welcome if not
  useEffect(() => {
    let cancelled = false;
    fetch(HEALTH_URL)
      .then((r) => (r.ok ? r.json() : null))
      .then((h) => {
        if (cancelled || h?.analytics?.status !== "disabled") return;
        setWelcome(WELCOME_SEARCH_ONLY);
        setAnalyticsOff(true);
        setMessages((m) => (m.length === 1 && m[0].id === 0 ? [WELCOME_SEARCH_ONLY] : m));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  // Reply state: null or { preview, type, quoted_context, quoted_filters, msgId }
  const [replyTo, setReplyTo] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const busyRef = useRef(false);       // a request is running (state can lag behind quick sends)
  const queueRef = useRef([]);         // messages sent while one was running, in order
  const requestRef = useRef(null);     // { controller, cancelled } of the running request
  const runRef = useRef(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  useEffect(() => {
    saveChat(sessionId, messages);
  }, [sessionId, messages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // ── Core send ──────────────────────────────────────────────────────────────
  // One request at a time: messages sent while an answer is loading wait in a queue (shown as
  // "Waiting…") and go out in order. Esc / Stop cancels the running request and the queue.
  const runRequest = useCallback(async ({ id, text, activeReply, newSearch }) => {
    busyRef.current = true;
    setLoading(true);
    setMessages((prev) => prev.map((m) => (m.id === id && m.queued ? { ...m, queued: false } : m)));

    // Build request body — include quoted context if replying (the AI provider is the server's choice)
    const body = {
      message: text,
      session_id: sessionId,
      ...chatContext(messagesRef.current),
    };
    // Suggestion chips are standalone queries — don't merge with previous filters
    if (newSearch) {
      body.new_search = true;
    }
    if (activeReply) {
      body.quoted_context = activeReply.quoted_context;
      body.quoted_type = activeReply.type;
      if (activeReply.quoted_filters) {
        body.quoted_filters = activeReply.quoted_filters;
      }
    }

    // A request that hangs is cancelled, so the input never stays locked
    const request = { controller: new AbortController(), cancelled: false };
    requestRef.current = request;
    const timer = setTimeout(() => request.controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const token = loginToken();
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
        signal: request.controller.signal,
      });

      // An error page from a proxy or a crashed server isn't JSON: read the text first
      const raw = await res.text();
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        data = null;
      }
      if (!res.ok) throw responseError(res.status, data);
      if (!data) throw new Error("The server sent an unexpected reply. Please try again.");

      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          id: Date.now(),
          data,
        },
      ]);

    } catch (err) {
      if (request.cancelled) {
        setMessages((prev) => [
          ...prev,
          { role: "bot", id: Date.now(), data: { type: "text", message: "Stopped. Ask again whenever you're ready." } },
        ]);
      } else {
        let message = err.message || "Something went wrong.";
        if (err.name === "AbortError") {
          message = "This is taking too long. Please try again in a moment.";
        } else if (err instanceof TypeError) {
          message = "Can't reach the server. Check your connection and try again.";
        }
        setMessages((prev) => [
          ...prev,
          {
            role: "bot",
            id: Date.now(),
            data: {
              type: "error",
              message,
              error_detail: err.detail || null,
              error_code: err.code || null,
            },
          },
        ]);
      }
    } finally {
      clearTimeout(timer);
      requestRef.current = null;
      const next = queueRef.current.shift();
      if (next) {
        runRef.current(next);
      } else {
        busyRef.current = false;
        setLoading(false);
      }
    }
  }, [sessionId]);
  runRef.current = runRequest;

  const sendMessage = useCallback((text, overrideReply, newSearch = false) => {
    const trimmed = (text || input).trim();
    if (!trimmed) return;

    // Capture the current reply state before clearing it
    const activeReply = overrideReply !== undefined ? overrideReply : replyTo;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const queued = busyRef.current;

    // Add user message to UI with optional reply preview
    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        id,
        text: trimmed,
        replyPreview: activeReply?.preview || null,
        replyType: activeReply?.type || null,
        queued,
      },
    ]);
    setInput("");
    setReplyTo(null);
    inputRef.current?.focus();

    const job = { id, text: trimmed, activeReply, newSearch };
    if (queued) {
      queueRef.current.push(job);
    } else {
      runRef.current(job);
    }
  }, [input, replyTo]);

  // Esc / Stop: cancel the answer being loaded and the messages waiting behind it
  const cancelRequest = useCallback(() => {
    const request = requestRef.current;
    if (!request) return;
    request.cancelled = true;
    const dropped = new Set(queueRef.current.map((job) => job.id));
    queueRef.current = [];
    if (dropped.size) {
      setMessages((prev) => prev.map((m) => (dropped.has(m.id) ? { ...m, queued: false, notSent: true } : m)));
    }
    request.controller.abort();
  }, []);

  useEffect(() => {
    if (!loading) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !replyTo) cancelRequest();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [loading, replyTo, cancelRequest]);

  const startNewChat = useCallback(() => {
    if (loading) return;
    const previousSessionId = sessionId;
    fetch(`${API_URL}/session?session_id=${encodeURIComponent(previousSessionId)}`, {
      method: "DELETE",
    }).catch(() => {});
    setSessionId(createSessionId());
    setMessages([welcome]);
    setInput("");
    setReplyTo(null);
  }, [loading, sessionId, welcome]);

  // ── Build reply context from a bot message ────────────────────────────────
  const handleReply = useCallback((msgId, data) => {
    const type = data.type === "stone_cards" ? "search"
               : data.type === "analytics_chart" ? "analytics"
               : "text";

    // Build a short text preview for the reply bar
    let preview = "";
    let quoted_context = "";

    if (type === "search") {
      const total = data.total_count || 0;
      const filters = data.filters_applied || {};
      const parts = [];
      if (filters.shape || filters.shapes?.length)
        parts.push(filters.shape || filters.shapes?.join(", "));
      if (filters.carat_min || filters.carat_max)
        parts.push(`${filters.carat_min || 0}–${filters.carat_max || "∞"}ct`);
      if (filters.price_max) parts.push(`under $${filters.price_max}`);
      if (filters.lab) parts.push(filters.lab);
      if (filters.diamond_status === "A") parts.push("available");
      const filterStr = parts.length ? parts.join(", ") : "all filters";
      preview = `${total.toLocaleString()} ${filterStr} diamonds found`;
      quoted_context = `Search result: ${total} diamonds with filters: ${JSON.stringify(filters)}`;
    } else if (type === "analytics") {
      preview = data.chart?.title || data.message?.slice(0, 80) || "Analytics chart";
      quoted_context = `Analytics result: "${preview}". Data summary: ${data.message?.slice(0, 200) || ""}`;
    } else {
      preview = (data.message || "").slice(0, 100);
      quoted_context = `Previous message: "${(data.message || "").slice(0, 300)}"`;
    }

    setReplyTo({
      msgId,
      preview,
      type,
      quoted_context,
      quoted_filters: type === "search" ? (data.filters_applied || null) : null,
    });

    // Focus input so user can type right away
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const handleKey = (e) => {
    if (e.key === "Escape" && replyTo) {
      setReplyTo(null);          // (Esc with no reply open stops a loading answer: see cancelRequest)
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="flex flex-col h-screen flex-1 min-w-0 bg-gray-50">

      {/* Header */}
      <div className="px-6 py-4 border-b bg-white flex items-center gap-3 shadow-sm">
        <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
          <span className="text-white text-xs font-bold">SG</span>
        </div>
        <div>
          <div className="font-semibold text-gray-900 text-sm">
            SolGuruz Diamonds — {analyticsOff ? "AI Assistant" : "AI Analytics"}
          </div>
          <div className="text-xs text-gray-400">Powered by real-time database</div>
        </div>
        <button
          type="button"
          onClick={startNewChat}
          disabled={loading}
          title="Start a new chat"
          className="ml-auto border border-gray-300 rounded-lg px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          New chat
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        {messages.map((msg, i) =>
          msg.role === "user" ? (
            <div key={msg.id || i} className="flex justify-end">
              <div className="max-w-sm">
                {/* Reply preview on user bubble */}
                {msg.replyPreview && (
                  <div className={`mb-1 px-3 py-1.5 rounded-lg text-xs border-l-2 ${
                    msg.replyType === "search" ? "bg-blue-50 border-blue-300 text-blue-700" :
                    msg.replyType === "analytics" ? "bg-purple-50 border-purple-300 text-purple-700" :
                    "bg-gray-100 border-gray-300 text-gray-600"
                  }`}>
                    <span className="opacity-70 mr-1">↩</span>
                    <span className="truncate">{msg.replyPreview}</span>
                  </div>
                )}
                <div className={`bg-black text-white px-4 py-2.5 rounded-2xl rounded-tr-sm text-sm leading-relaxed ${
                  msg.queued || msg.notSent ? "opacity-60" : ""}`}>
                  {msg.text}
                </div>
                {msg.queued && (
                  <div className="text-[11px] text-gray-400 mt-1 text-right">Waiting — sends after the current answer</div>
                )}
                {msg.notSent && (
                  <div className="text-[11px] text-red-400 mt-1 text-right">Not sent</div>
                )}
              </div>
            </div>
          ) : (
            <div key={msg.id || i} className="flex justify-start items-start gap-3 group">
              <div className="w-7 h-7 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white text-xs">AI</span>
              </div>
              <div className="flex-1">
                <BotResponse
                  data={msg.data}
                  // Chips that refine the search shown ("Try E-F color range") build on it;
                  // other chips start fresh
                  onChipClick={(chip) => sendMessage(chip, null, !msg.data?.chips_refine)}
                  onClearFilters={() => {
                    // Re-run the question that produced this answer, without earlier filters
                    const question = messages.slice(0, i).reverse().find((m) => m.role === "user");
                    if (question) sendMessage(question.text, null, true);
                  }}
                  onReply={() => handleReply(msg.id || i, msg.data)}
                  onShowMore={() => {
                    const filters = msg.data?.filters_applied;
                    if (filters && Object.keys(filters).length > 0 && onOpenSearch) {
                      onOpenSearch(filters);
                    } else {
                      sendMessage("show me more results from this search");
                    }
                  }}
                />
              </div>
            </div>
          )
        )}

        {/* Loading dots */}
        {loading && (
          <div className="flex justify-start items-start gap-3">
            <div className="w-7 h-7 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs">AI</span>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 shadow-sm">
              <div className="flex gap-1.5 items-center">
                {[0, 150, 300].map((delay) => (
                  <span key={delay} className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: `${delay}ms` }} />
                ))}
              </div>
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Reply bar (shows above input when replying) */}
      <ReplyBar reply={replyTo} onCancel={() => setReplyTo(null)} />

      {/* Input bar */}
      <div className="px-6 py-4 border-t bg-white shadow-sm">
        <div className="flex gap-3 items-end">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
            }}
            onKeyDown={handleKey}
            maxLength={MAX_MESSAGE_LENGTH}
            rows={1}
            className="flex-1 border border-gray-300 rounded-xl px-4 py-3 text-sm outline-none focus:border-gray-600 transition resize-none disabled:opacity-50 leading-relaxed"
            placeholder={replyTo ? `Reply about "${replyTo.preview.slice(0, 40)}…"`
              : analyticsOff ? "Search diamonds, look up a stock ID, or compare stones…"
              : "Ask about sales, inventory, holds, or find diamonds…"}
            style={{ minHeight: "44px", maxHeight: "120px" }}
          />
          {loading && !input.trim() ? (
            <button
              onClick={cancelRequest}
              title="Stop this answer (Esc)"
              className="border border-gray-300 text-gray-700 px-5 py-3 rounded-xl text-sm font-medium hover:bg-gray-50 transition flex-shrink-0"
            >
              Stop
            </button>
          ) : (
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim()}
              className="bg-black text-white px-5 py-3 rounded-xl text-sm font-medium disabled:opacity-40 hover:bg-gray-800 transition flex-shrink-0"
            >
              Send
            </button>
          )}
        </div>
        <p className="text-xs text-gray-400 mt-1.5 text-center">
          Press Enter to send · Shift+Enter for new line · Esc to stop an answer or cancel a reply
          {input.length > MAX_MESSAGE_LENGTH * 0.9 && (
            <span className={input.length >= MAX_MESSAGE_LENGTH ? "text-red-500" : ""}>
              {" · "}{input.length.toLocaleString()} / {MAX_MESSAGE_LENGTH.toLocaleString()}
            </span>
          )}
        </p>
      </div>
    </div>
  );
}